import React, { useEffect, useState, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Paper, 
  Typography, 
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Divider
} from '@mui/material';
import { 
  SwapHoriz as SwapIcon, 
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Star as StarIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import MessageButton from '../components/MessageButton';
import { API_BASE_URL } from '../utils/api';

const API_BASE = API_BASE_URL;

interface TransferRequest {
  id: number;
  userId: number;
  profession: string;
  currentLocation: string;
  targetLocations: string[];
  notes?: string;
  User: {
    id: number;
    name: string;
    email: string;
  };
}

interface TransferMatch {
  id: number;
  aRequestId: number;
  bRequestId: number;
  score: number;
  status: string;
  createdAt: string;
  TransferRequest_TransferMatch_aRequestIdToTransferRequest: TransferRequest;
  TransferRequest_TransferMatch_bRequestIdToTransferRequest: TransferRequest;
}

const TransferHubMatches: React.FC = () => {
  const { token, user } = useAuth();
  const [matches, setMatches] = useState<TransferMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string>('');

  const load = useCallback(async () => {
    try {
    const res = await fetch(`${API_BASE}/transferhub/matches`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setMatches(data);
      }
    } catch (error) {
      console.error('Failed to load matches:', error);
    }
  }, [token]);

  const run = async () => {
    setLoading(true);
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/transferhub/match/run`, { 
        method: 'POST', 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if (res.ok) {
        setMessage('Matching completed successfully!');
    await load();
      } else {
        setMessage('Failed to run matching algorithm');
      }
    } catch (error) {
      setMessage('Failed to run matching algorithm');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const getMatchScoreColor = (score: number) => {
    if (score >= 120) return 'success';
    if (score >= 110) return 'warning';
    return 'default';
  };

  const getMatchScoreLabel = (score: number) => {
    if (score >= 120) return 'Excellent Match';
    if (score >= 110) return 'Good Match';
    return 'Basic Match';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          <SwapIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Transfer Matches
        </Typography>
        <Button 
          variant="contained" 
          onClick={run} 
          disabled={loading}
          startIcon={<RefreshIcon />}
        >
          {loading ? 'Running...' : 'Find Matches'}
        </Button>
      </Box>

      {message && (
        <Alert sx={{ mb: 3 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      {matches.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <SwapIcon sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No matches found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Click "Find Matches" to run the matching algorithm and discover potential transfer opportunities.
            </Typography>
            <Button variant="contained" onClick={run} disabled={loading}>
              Find Matches
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {matches.map(match => {
            const isMyRequestA = match.TransferRequest_TransferMatch_aRequestIdToTransferRequest.User.email === user?.email;
            const myRequest = isMyRequestA ? match.TransferRequest_TransferMatch_aRequestIdToTransferRequest : match.TransferRequest_TransferMatch_bRequestIdToTransferRequest;
            const otherRequest = isMyRequestA ? match.TransferRequest_TransferMatch_bRequestIdToTransferRequest : match.TransferRequest_TransferMatch_aRequestIdToTransferRequest;
            
            return (
              <Grid item xs={12} key={match.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Transfer Match #{match.id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Found on {new Date(match.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${getMatchScoreLabel(match.score)} (${match.score}%)`}
                        color={getMatchScoreColor(match.score)}
                        icon={<StarIcon />}
                      />
                    </Box>

                    <Grid container spacing={3}>
                      {/* My Request */}
                      <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 1 }} />
                            Your Request
                          </Typography>
                          <Typography variant="subtitle1" gutterBottom>
                            {myRequest.User.name}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <WorkIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1rem' }} />
                            {myRequest.profession}
                          </Typography>
                          <Box sx={{ mb: 1 }}>
                            <LocationIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1rem' }} />
                            <Typography variant="body2" component="span">
                              From: {myRequest.currentLocation}
                            </Typography>
                          </Box>
                          <Typography variant="body2">
                            To: {myRequest.targetLocations.join(', ')}
                          </Typography>
                          {myRequest.notes && (
                            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                              "{myRequest.notes}"
                            </Typography>
                          )}
                        </Paper>
                      </Grid>

                      {/* Swap Icon */}
                      <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SwapIcon sx={{ fontSize: '3rem', color: 'primary.main' }} />
                      </Grid>

                      {/* Other Request */}
                      <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 2, bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 1 }} />
                            Potential Match
                          </Typography>
                          <Typography variant="subtitle1" gutterBottom>
                            {otherRequest.User.name}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <WorkIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1rem' }} />
                            {otherRequest.profession}
                          </Typography>
                          <Box sx={{ mb: 1 }}>
                            <LocationIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1rem' }} />
                            <Typography variant="body2" component="span">
                              From: {otherRequest.currentLocation}
                            </Typography>
                          </Box>
                          <Typography variant="body2">
                            To: {otherRequest.targetLocations.join(', ')}
                          </Typography>
                          {otherRequest.notes && (
                            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                              "{otherRequest.notes}"
                            </Typography>
                          )}
          </Paper>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Match Status: <Chip label={match.status} size="small" />
                      </Typography>
                      {(() => {
                        const otherUserId = isMyRequestA
                          ? match.TransferRequest_TransferMatch_bRequestIdToTransferRequest.User.id
                          : match.TransferRequest_TransferMatch_aRequestIdToTransferRequest.User.id;
                        const otherUserName = isMyRequestA
                          ? match.TransferRequest_TransferMatch_bRequestIdToTransferRequest.User.name
                          : match.TransferRequest_TransferMatch_aRequestIdToTransferRequest.User.name;
                        return (
                          <MessageButton
                            receiverId={otherUserId}
                            receiverName={otherUserName}
                            variant="outlined"
                            size="small"
                            startIcon
                          />
                        );
                      })()}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default TransferHubMatches;